<template>
  <div id="dividends-by-month">
    <v-card class="px-4 pt-2 pb-4 my-0 elevation-0">
      <v-card-title class="px-1">
        <h1 class="card-title">
          <template v-if="showDateTypeSelector">
            {{ $t('views.dashboardDetail.dividends.dividendsByMonth.title') }}
            <v-chip-group v-model="dateType" active-class="primary" mandatory column class="ml-3">
              <v-chip filter value="month" small class="mx-0">
                {{ $t('common.month') }}
              </v-chip>
              <span class="mx-1">/</span>
              <v-chip filter value="year" small class="mx-0">
                {{ $t('common.year') }}
              </v-chip>
            </v-chip-group>
          </template>
          <template v-else>
            {{ $t('views.dashboardDetail.dividends.dividendsByMonth.title') }}
            {{ $t(`common.${dateType}`) }}
          </template>
        </h1>
      </v-card-title>
      <v-card-text class="mt-4 pa-0">
        <BarLoading v-if="!showTable && !loadedData" />
        <BarChart
          v-if="loadedData"
          ref="barChart"
          :chartData="barChartData"
          :chartOptions="barChartOptions"
        />

        <template v-if="showTable">
          <div class="my-5" />
          <v-data-table
            id="dividends-by-month-table"
            :headers="headers"
            :items="mySummary"
            class="elevation-0 left-sticky-table"
            mobile-breakpoint="0"
            :loading="!loadedData"
            :items-per-page="5"
            :page.sync="currentPage"
            :item-class="itemRowBackground"
            :dense="user.preferences.denseTables"
          >
            <template slot="body.prepend">
              <tr v-if="totalRow" class="row-total">
                <td>TOTAL</td>
                <td class="text-right">
                  <currency :value="totalRow.totalBaseCurrency" :currency="user.baseCurrency" />
                </td>
                <td class="text-right">
                  <currency
                    :value="totalRow.originRetentionBaseCurrency"
                    :currency="user.baseCurrency"
                  />
                </td>
                <td class="text-right">
                  <currency :value="totalRow.netoBaseCurrency" :currency="user.baseCurrency" />
                </td>
                <td class="text-right">
                  <currency
                    :value="totalRow.destinationRetentionBaseCurrency"
                    :currency="user.baseCurrency"
                  />
                </td>
                <td class="text-right">
                  <currency :value="totalRow.totalNetoBaseCurrency" :currency="user.baseCurrency" />
                </td>
                <td class="text-right">
                  <currency
                    :value="totalRow.retentionReturnedBaseCurrency"
                    :currency="user.baseCurrency"
                  />
                </td>
                <td class="text-right">
                  <currency
                    :value="totalRow.netoWithReturnBaseCurrency"
                    :currency="user.baseCurrency"
                    strong
                  />
                </td>
                <td v-if="dateType === 'year'" class="text-right"></td>
              </tr>
            </template>
            <template v-slot:item._id="{ item }">
              <div class="items-flex">
                <span>
                  <template v-if="dateType === 'month'">
                    {{ $t(`common.months.${item._id.month}`) }} {{ item._id.year }}
                  </template>
                  <template v-else>
                    {{ item._id }}
                  </template>
                </span>
                <template v-if="someNullExchangeRate(item)">
                  <div class="ml-3">
                    <WarningIcon
                      :text="$t(`views.dashboardDetail.dividends.dividendsByMonth.warning`)"
                    />
                  </div>
                </template>
              </div>
            </template>
            <template v-slot:item.totalBaseCurrency="{ item }">
              <currency :value="item.totalBaseCurrency" :currency="user.baseCurrency" />
            </template>
            <template v-slot:item.retentionReturnedBaseCurrency="{ item }">
              <currency :value="item.retentionReturnedBaseCurrency" :currency="user.baseCurrency" />
            </template>
            <template v-slot:item.originRetentionBaseCurrency="{ item }">
              <currency :value="item.originRetentionBaseCurrency" :currency="user.baseCurrency" />
            </template>
            <template v-slot:item.netoBaseCurrency="{ item }">
              <currency :value="item.netoBaseCurrency" :currency="user.baseCurrency" />
            </template>
            <template v-slot:item.destinationRetentionBaseCurrency="{ item }">
              <currency
                :value="item.destinationRetentionBaseCurrency"
                :currency="user.baseCurrency"
              />
            </template>
            <template v-slot:item.totalNetoBaseCurrency="{ item }">
              <currency :value="item.totalNetoBaseCurrency" :currency="user.baseCurrency" />
            </template>
            <template v-slot:item.netoWithReturnBaseCurrency="{ item }">
              <currency
                :value="item.netoWithReturnBaseCurrency"
                :currency="user.baseCurrency"
                strong
              />
            </template>
            <template v-if="dateType === 'year'" v-slot:item.totalDividendYield="{ item }">
              <div class="items-flex justify-end">
                <currency
                  v-if="item.totalDividendYield"
                  :value="item.totalDividendYield"
                  currency="%"
                  neverHide
                />
                <span v-else></span>
                <v-tooltip top max-width="300px">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" size="12" class="ml-1" v-on="on">
                      mdi-information
                    </v-icon>
                  </template>
                  <span>
                    {{
                      $t('views.dashboardDetail.dividends.dividendsByMonth.totalDividendYieldInfo')
                    }}
                  </span>
                </v-tooltip>
              </div>
            </template>
          </v-data-table>
        </template>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { map } from 'lodash'
import { mapState } from 'vuex'
import { getDividendSummary } from '@/lib/api.js'
import { DARK_DEFAULT_PALETTE, DEFAULT_PALETTE } from '@/lib/colors.js'
import { getCurrencyValueAsString, getCurrencySymbol } from '@/lib/utils/currencies.js'
import BarChart from '@/views/components/core/chartjs/BarChart'
import BarLoading from '@/views/components/core/BarLoading'
import Currency from '@/views/components/common/Currency'
import WarningIcon from '@/views/components/common/WarningIcon.vue'

export default {
  components: { BarChart, BarLoading, Currency, WarningIcon },
  props: {
    showTable: {
      type: Boolean,
      default: true,
    },
    showDateTypeSelector: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    summary: [],
    oneYearBeforeSummary: [],
    mySummary: [],
    myOneYearBeforeSummary: [],
    totalRow: null,
    currentPage: null,
    loadedData: false,
    dateType: 'month',
    barChartData: {
      labels: null,
      datasets: null,
    },
    isAllDate: false,
  }),
  computed: {
    ...mapState(['user', 'hideAmounts']),
    barChartOptions() {
      const context = this
      const isAllDate = this.isAllDate
      return {
        maintainAspectRatio: false,
        legend: {
          display: false,
        },
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false,
              },
            },
          ],
          yAxes: [
            {
              ticks: {
                callback: function (value) {
                  return getCurrencyValueAsString({
                    value,
                    currency: context.user.baseCurrency,
                    hide: context.hideAmounts,
                  })
                },
              },
            },
          ],
        },
        tooltips: {
          callbacks: {
            label: function (tooltipItem, data) {
              let label = data.datasets[tooltipItem.datasetIndex].label || ''
              if (label) label += ': '
              label += getCurrencyValueAsString({
                value: tooltipItem.yLabel,
                currency: context.user.baseCurrency,
                hide: context.hideAmounts,
              })
              return label
            },
            title: function (tooltipItem) {
              const label = tooltipItem[0].label
              const labelSplitted = label.split(' ')
              if (
                isAllDate || // si es all date, no hay comparaciones
                labelSplitted.length <= 1 ||
                tooltipItem[0].datasetIndex === 1
              ) {
                return label
              } else {
                return `${labelSplitted[0]} ${parseInt(labelSplitted[1]) - 1}` // Dic 21 -> Dic 20
              }
            },
          },
        },
      }
    },
    chartData() {
      const summary = this.mySummary.slice().reverse()
      let labels
      if (this.dateType === 'month') {
        labels = map(
          summary,
          (item) =>
            this.$t(`common.months.${item._id.month}`).substring(0, 3) +
            ` ${item._id.year.toString().slice(-2)}`
        )
      } else {
        labels = map(summary, (item) => item._id)
      }

      const series = [map(summary, 'netoWithReturnBaseCurrency')]
      return {
        labels,
        series,
      }
    },
    headers() {
      const headers = [
        {
          text:
            this.dateType === 'month'
              ? this.$t('views.dividends.dividendTable.month')
              : this.$t('views.dividends.dividendTable.year'),
          value: '_id',
          sortable: false,
          divider: true,
        },
        {
          text: `${this.$t('views.dividends.dividendTable.total')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'totalBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t(
            'views.dividends.dividendTable.totalOriginRetention'
          )} (${getCurrencySymbol(this.user.baseCurrency)})`,
          value: 'originRetentionBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.dividends.dividendTable.neto')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'netoBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t(
            'views.dividends.dividendTable.destinationRetention'
          )} (${getCurrencySymbol(this.user.baseCurrency)})`,
          value: 'destinationRetentionBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.dividends.dividendTable.totalNeto')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'totalNetoBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.dividends.dividendTable.retentionReturned')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'retentionReturnedBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.dividends.dividendTable.netoWithReturn')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'netoWithReturnBaseCurrency',
          align: 'right',
        },
      ]

      if (this.dateType === 'year') {
        headers.push({
          text: this.$t('views.dividends.dividendTable.totalDividendYield'),
          value: 'totalDividendYield',
          align: 'right',
        })
      }

      return headers
    },
  },
  watch: {
    dateType: {
      handler() {
        this.changeDateType()
      },
    },
  },
  methods: {
    someNullExchangeRate(item) {
      return (
        item.exchangeRates &&
        item.exchangeRates.some((elem) => {
          if (Array.isArray(elem)) return elem.some((subElem) => subElem === null)
          return elem === null
        })
      )
    },
    itemRowBackground(item) {
      if (this.someNullExchangeRate(item)) return 'row-advise'
    },
    createBarChartObject() {
      const summary = this.mySummary.slice().reverse()
      const oneYearBeforeSummary = this.myOneYearBeforeSummary.slice().reverse()

      const barChartDataSets = [
        {
          label: `${this.$t('views.dividends.dividendTable.netoWithReturn')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          data: map(summary, 'netoWithReturnBaseCurrency'),
          backgroundColor: this.$vuetify.theme.dark
            ? DARK_DEFAULT_PALETTE.chartsBarBlue
            : DEFAULT_PALETTE.chartsBarBlue,
        },
      ]
      if (oneYearBeforeSummary && oneYearBeforeSummary.length) {
        barChartDataSets.unshift({
          label: `${this.$t('views.dividends.dividendTable.netoWithReturn')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          data: map(oneYearBeforeSummary, 'netoWithReturnBaseCurrency'),
          backgroundColor: this.$vuetify.theme.dark
            ? DARK_DEFAULT_PALETTE.dark_Textgrey
            : DEFAULT_PALETTE.darkBackgroundGrey,
        })
      }
      this.barChartData.datasets = barChartDataSets

      let labels
      let summaryToGetLabels = summary
      if (
        (!summaryToGetLabels || !summaryToGetLabels.length) &&
        oneYearBeforeSummary &&
        oneYearBeforeSummary.length
      ) {
        summaryToGetLabels = oneYearBeforeSummary
      }
      if (this.dateType === 'month') {
        labels = map(
          summaryToGetLabels,
          (item) =>
            this.$t(`common.months.${item._id.month}`).substring(0, 3) +
            ` ${item._id.year.toString().slice(-2)}`
        )
      } else {
        labels = map(summaryToGetLabels, (item) => item._id)
      }
      this.barChartData.labels = labels

      if (this.$refs.barChart) this.$refs.barChart.render()
    },
    changeDateType() {
      const summary = JSON.parse(JSON.stringify(this.summary))
      const oneYearBeforeSummary = JSON.parse(JSON.stringify(this.oneYearBeforeSummary))

      this.totalRow = null
      this.mySummary = []
      this.myOneYearBeforeSummary = []

      if (this.dateType === 'month') {
        if (summary.length) {
          let summaryMonths = [summary[0]]
          summary.forEach((year) => {
            if (year.months) summaryMonths = summaryMonths.concat(year.months)
          })
          this.totalRow = summaryMonths.shift()
          this.mySummary = summaryMonths
        }

        if (oneYearBeforeSummary.length) {
          let summaryOneYearBeforeMonths = [oneYearBeforeSummary[0]]
          oneYearBeforeSummary.forEach((year) => {
            if (year.months)
              summaryOneYearBeforeMonths = summaryOneYearBeforeMonths.concat(year.months)
          })
          summaryOneYearBeforeMonths.shift()
          this.myOneYearBeforeSummary = this.mySummary.map((elem) =>
            summaryOneYearBeforeMonths.find(
              (item) => elem._id.month === item._id.month && elem._id.year - 1 === item._id.year
            )
          )
        }
      } else {
        this.totalRow = summary.shift()
        this.mySummary = summary
      }
      this.currentPage = 1

      this.createBarChartObject()
    },
    async loadSummary(filters) {
      this.loadedData = false
      try {
        const summary = await getDividendSummary({
          group: 'byMonth',
          filter: {
            ...filters,
          },
        })
        this.summary = summary.data.data
        this.oneYearBeforeSummary = summary.data.oneYearBeforeData
          ? summary.data.oneYearBeforeData
          : []

        this.isAllDate = !filters.date || Object.keys(filters.date).length === 0
        if (this.isAllDate) {
          this.$set(this, 'dateType', 'year')
        } else {
          this.$set(this, 'dateType', 'month')
        }

        this.changeDateType()
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }
      this.loadedData = true
    },
  },
}
</script>

<style lang="scss">
#dividends-by-month {
  .card-title {
    display: flex;
    align-items: center;
  }

  #dividends-by-month-table {
    .items-flex {
      display: flex;
      &.justify-end {
        justify-content: flex-end;
      }
    }
  }
}
</style>
